import { Text } from 'slate';

import type { AlineText, ParagraphChildElement } from '../../slate-api';
import { isCheckboxElement, isCheckboxOptionFieldElement, isLinkElement } from '../../slate-api';

/**
 * Yields every AlineText descendant of a paragraph's inline children, in
 * document order, descending through inline wrapper elements (links and the
 * rest) to any depth.
 *
 * Text nodes are detected with `Text.isText`, which keys only off the `text`
 * field. Detecting them by the presence of `fontSize` (or any other styling
 * field) is wrong: a real document run may lack `fontSize` and is still a text
 * node, not an inline element with children. Treating it as the latter is what
 * crashed this walk on `node.children`.
 *
 * The `satisfies never` is a deliberate compile-time guard. Today every inline
 * element is either text, a link (whose children we recurse into), or a wrapper
 * whose own children are text-only. If a new inline element type is ever given
 * a non-text, non-link child, that residual stops being `never` and this line
 * fails to compile — a signal to teach this walk (and the callers that flatten
 * through it) how to handle the new nesting rather than silently dropping it.
 */
export function* iterateInlineTextNodes(nodes: ParagraphChildElement[]): Generator<AlineText> {
  for (const node of nodes) {
    if (Text.isText(node)) {
      yield node;
      continue;
    }
    if (isLinkElement(node) || isCheckboxElement(node) || isCheckboxOptionFieldElement(node)) {
      yield* iterateInlineTextNodes(node.children);
      continue;
    }
    for (const inlineChild of node.children) {
      if (Text.isText(inlineChild)) {
        yield inlineChild;
        continue;
      }
      inlineChild satisfies never;
    }
  }
}
